import type { useStoreRefs } from "@/hooks/useStoreRefs";
import { screenToWorld } from "@/lib/camera";
import { getDecorationDrawDimensions } from "@/lib/drawHexes";
import { getBroadcastMapChange } from "@/state/realtimeBroadcast";
import { MIN_DECORATION_SCATTER_RADIUS } from "@/state/useEditorStore";
import type { Hex, HexData, HexEdge, MapChangeSnapshot, MapDecoration } from "@west-marches/shared";
import {
  DEFAULT_SECONDARY_PAINT_TILE_SET_ID,
  OPPOSITE_EDGE,
  getDecorationImage,
  getDecorationPaintSetById,
  getDefaultTileIdFromTileSet,
  getEdgeBetween,
  getRandomDecorationIdFromPaintSet,
  getRandomTileIdFromTileSet,
  getTileSetById,
  hexKey,
  hexNeighbors,
  hexToPixel,
  isDynamicDecorationPaintSet,
  isDynamicTileSet,
  pixelToHex,
  resolveBrushEntryToPaintSelection,
  resolveDecorationBrushEntryToDecorationId,
  type DecorationBrushEntry,
  type OrderedBrushEntry,
  type OverlayPaintSet,
  type TileSet,
} from "@west-marches/shared";
import { useRef } from "react";

export interface EdgeScrollHandle {
  updateEdgeScroll: (x: number, y: number) => void;
  stopEdgeScroll: () => void;
  lastMouseRef: React.MutableRefObject<{
    clientX: number;
    clientY: number;
  } | null>;
}

interface UseCanvasHandlersOpts {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  cameraRef: React.MutableRefObject<{ x: number; y: number; zoom: number }>;
  latestRef: ReturnType<typeof useStoreRefs>;
  hoveredHexRef: React.MutableRefObject<string | null>;
  pointerWorldRef: React.MutableRefObject<{
    x: number;
    y: number;
    inside: boolean;
  }>;
  spaceHeldRef: React.MutableRefObject<boolean>;
  edgeScrollRef: React.MutableRefObject<EdgeScrollHandle | null>;
  draw: () => void;
  updateCursor: () => void;
  setCursorPreview: React.Dispatch<
    React.SetStateAction<{
      clientX: number;
      clientY: number;
      visible: boolean;
      hoveredTileSetId: string | null;
    }>
  >;
}

export function shouldHighlightHoveredHex(activeTool: string): boolean {
  return (
    activeTool === "tile-brush" ||
    activeTool === "decoration-brush" ||
    activeTool === "map-note" ||
    activeTool === "eyedropper"
  );
}

export function getPaintTileSetId(button: number, brush: string | null): string | null {
  if (button === 0) {
    return brush;
  }
  if (button === 2) {
    return DEFAULT_SECONDARY_PAINT_TILE_SET_ID;
  }
  return null;
}

/**
 * Resolve the active decoration paint set + brush entry to a single
 * overlay id to place. For dynamic paint sets, this draws a fresh random
 * member each call so a single drag stroke can scatter different
 * decorations. For ordered/single sets, the currently-selected brush
 * entry decides which overlay paints. Returns null when the selection is
 * stale or empty.
 */
export function resolveActiveDecorationId(
  paintSet: OverlayPaintSet | null,
  brushEntry: DecorationBrushEntry | null,
  fallbackDecorationId: string | null,
): string | null {
  if (paintSet) {
    if (isDynamicDecorationPaintSet(paintSet)) {
      return getRandomDecorationIdFromPaintSet(paintSet) ?? fallbackDecorationId;
    }
    if (brushEntry) {
      return (
        resolveDecorationBrushEntryToDecorationId(brushEntry) ?? fallbackDecorationId
      );
    }
  }
  return fallbackDecorationId;
}

export function getPaintSelection(
  group: TileSet,
  selectedEntry: OrderedBrushEntry | null,
): { tileId: string; tileSetId: string } | null {
  if (group.tileIds.length === 0) {
    return null;
  }

  if (isDynamicTileSet(group)) {
    return {
      tileId: getRandomTileIdFromTileSet(group),
      tileSetId: group.id,
    };
  }

  if (selectedEntry?.kind === "tile" && group.tileIds.includes(selectedEntry.tileId)) {
    return {
      tileId: selectedEntry.tileId,
      tileSetId: group.id,
    };
  }

  const contextualSelection = resolveBrushEntryToPaintSelection(selectedEntry, group.id);
  if (contextualSelection) {
    return contextualSelection;
  }

  const defaultTileId = getDefaultTileIdFromTileSet(group);
  return defaultTileId ? { tileId: defaultTileId, tileSetId: group.id } : null;
}

export function createDecorationPlacementHistoryEntry(decoration: MapDecoration): {
  before: MapChangeSnapshot;
  after: MapChangeSnapshot;
} {
  return {
    before: {
      decorations: {
        [decoration.id]: null,
      },
    },
    after: {
      decorations: {
        [decoration.id]: decoration,
      },
    },
  };
}

function getDecorationScatterJitter(radius: number): number {
  return Math.max(26, radius * 1.15);
}

function getDecorationScatterSpacing(radius: number, density: number): number {
  return Math.max(36, radius * (1.8 - density * 0.06));
}

function getDecorationScatterAttempts(
  radius: number,
  density: number,
  initialBurst: boolean,
): number {
  if (radius <= MIN_DECORATION_SCATTER_RADIUS) {
    return 1;
  }

  return Math.max(1, Math.ceil(density * 0.22)) + (initialBurst ? 1 : 0);
}

function createDecorationPlacementBatchHistoryEntry(decorations: MapDecoration[]): {
  before: MapChangeSnapshot;
  after: MapChangeSnapshot;
} {
  const before: NonNullable<MapChangeSnapshot["decorations"]> = {};
  const after: NonNullable<MapChangeSnapshot["decorations"]> = {};

  for (const decoration of decorations) {
    before[decoration.id] = null;
    after[decoration.id] = decoration;
  }

  return {
    before: { decorations: before },
    after: { decorations: after },
  };
}

function getDistanceBetweenPoints(
  first: { x: number; y: number },
  second: { x: number; y: number },
): number {
  return Math.hypot(second.x - first.x, second.y - first.y);
}

function getDecorationWorldCenter(decoration: MapDecoration): { x: number; y: number } {
  const hexCenter = hexToPixel({ q: decoration.q, r: decoration.r });
  return {
    x: hexCenter.x + decoration.offsetX,
    y: hexCenter.y + decoration.offsetY,
  };
}

export function getDecorationEraseTarget(
  decorations: MapDecoration[],
  pointer: { x: number; y: number },
): MapDecoration | null {
  for (let index = decorations.length - 1; index >= 0; index -= 1) {
    const decoration = decorations[index];
    if (!decoration) {
      continue;
    }
    const image = getDecorationImage(decoration.decorationId);
    const { width, height } = getDecorationDrawDimensions(
      image ?? { naturalWidth: 0, naturalHeight: 0 },
    );
    const center = getDecorationWorldCenter(decoration);
    const withinBounds =
      pointer.x >= center.x - width / 2 &&
      pointer.x <= center.x + width / 2 &&
      pointer.y >= center.y - height / 2 &&
      pointer.y <= center.y + height / 2;

    if (withinBounds) {
      return decoration;
    }
  }

  return null;
}

export function getDecorationEraseTargets(
  decorations: MapDecoration[],
  pointer: { x: number; y: number },
  radius: number,
): MapDecoration[] {
  const radiusSquared = radius * radius;

  return decorations.filter((decoration) => {
    const center = getDecorationWorldCenter(decoration);
    const dx = center.x - pointer.x;
    const dy = center.y - pointer.y;
    return dx * dx + dy * dy <= radiusSquared;
  });
}

export function useCanvasHandlers(opts: UseCanvasHandlersOpts) {
  const {
    canvasRef,
    cameraRef,
    latestRef,
    hoveredHexRef,
    pointerWorldRef,
    spaceHeldRef,
    edgeScrollRef,
    draw,
    updateCursor,
    setCursorPreview,
  } = opts;

  const dragRef = useRef<{
    startX: number;
    startY: number;
    camX: number;
    camY: number;
    moved: number;
    button: number;
  } | null>(null);

  const strokeRef = useRef<{
    before: MapChangeSnapshot;
    after: MapChangeSnapshot;
    paintedKeys: Set<string>;
  } | null>(null);

  const roadStrokeRef = useRef<{
    before: MapChangeSnapshot;
    after: MapChangeSnapshot;
    lastHex: Hex | null;
    modifiedKeys: Set<string>;
  } | null>(null);

  const decorationEraseStrokeRef = useRef<{
    before: MapChangeSnapshot;
    after: MapChangeSnapshot;
    erasedDecorationIds: Set<string>;
  } | null>(null);

  const decorationPaintStrokeRef = useRef<{
    placements: MapDecoration[];
    placementKeys: Set<string>;
    lastScatterPoint: { x: number; y: number } | null;
  } | null>(null);

  // --- Paint helpers ---

  function createDecorationInstance(hex: Hex, decorationId: string, wx: number, wy: number): MapDecoration {
    const center = hexToPixel(hex);
    const uniqueId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `decoration-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return {
      id: uniqueId,
      decorationId,
      q: hex.q,
      r: hex.r,
      offsetX: wx - center.x,
      offsetY: wy - center.y,
    };
  }

  function getDecorationsForHex(targetHex: Hex): MapDecoration[] {
    const key = hexKey(targetHex);
    return Object.values(latestRef.current.mapState.decorations).filter(
      (decoration) => hexKey({ q: decoration.q, r: decoration.r }) === key,
    );
  }

  function getScatterMinimumDistance(decorationId: string, radius: number): number {
    const image = getDecorationImage(decorationId);
    if (!image) {
      return Math.max(12, radius * 0.3);
    }
    const { width, height } = getDecorationDrawDimensions(image);
    return Math.max(12, Math.min(width, height) * 0.45);
  }

  function hasDecorationNearPoint(
    point: { x: number; y: number },
    minimumDistance: number,
    placementKeys: Set<string>,
  ): boolean {
    const minimumDistanceSquared = minimumDistance * minimumDistance;

    for (const decoration of Object.values(latestRef.current.mapState.decorations)) {
      const center = getDecorationWorldCenter(decoration);
      const dx = center.x - point.x;
      const dy = center.y - point.y;
      if (dx * dx + dy * dy < minimumDistanceSquared) {
        return true;
      }
    }

    for (const key of placementKeys) {
      const [xString, yString] = key.split(":");
      const dx = Number(xString) - point.x;
      const dy = Number(yString) - point.y;
      if (dx * dx + dy * dy < minimumDistanceSquared) {
        return true;
      }
    }

    return false;
  }

  function recordDecorationPlacement(decoration: MapDecoration) {
    const stroke = decorationPaintStrokeRef.current;
    if (!stroke) {
      return;
    }

    stroke.placements.push(decoration);
    stroke.placementKeys.add(`${Math.round(getDecorationWorldCenter(decoration).x)}:${Math.round(getDecorationWorldCenter(decoration).y)}`);
    latestRef.current.applyMapChanges({
      decorations: {
        [decoration.id]: decoration,
      },
    });
  }

  function placeSingleDecorationAtPoint(origin: { x: number; y: number }, decorationId: string) {
    const targetHex = pixelToHex(origin.x, origin.y);
    const key = hexKey(targetHex);
    if (!latestRef.current.mapState.hexes[key]) {
      return;
    }

    recordDecorationPlacement(createDecorationInstance(targetHex, decorationId, origin.x, origin.y));
  }

  function scatterDecorationsAtPoint(
    origin: { x: number; y: number },
    fixedDecorationId: string,
    radius: number,
    density: number,
    initialBurst: boolean,
    paintSet: OverlayPaintSet | null = null,
    brushEntry: DecorationBrushEntry | null = null,
    fallbackDecorationId: string | null = null,
  ) {
    const stroke = decorationPaintStrokeRef.current;
    if (!stroke) {
      return;
    }

    const attempts = getDecorationScatterAttempts(radius, density, initialBurst);
    const jitter = getDecorationScatterJitter(radius);
    const minimumDistance = getScatterMinimumDistance(fixedDecorationId, radius);
    const dynamic = paintSet ? isDynamicDecorationPaintSet(paintSet) : false;

    for (let index = 0; index < attempts; index += 1) {
      const angle = Math.random() * Math.PI * 2;
      const distance =
        index === 0 ? 0 : Math.sqrt(Math.random()) * Math.min(radius, jitter);
      const targetPoint = {
        x: origin.x + Math.cos(angle) * distance,
        y: origin.y + Math.sin(angle) * distance,
      };
      const targetHex = pixelToHex(targetPoint.x, targetPoint.y);
      const key = hexKey(targetHex);
      if (!latestRef.current.mapState.hexes[key]) {
        continue;
      }
      if (hasDecorationNearPoint(targetPoint, minimumDistance, stroke.placementKeys)) {
        continue;
      }

      const placementDecorationId = dynamic
        ? resolveActiveDecorationId(paintSet, brushEntry, fallbackDecorationId) ??
          fixedDecorationId
        : fixedDecorationId;

      recordDecorationPlacement(
        createDecorationInstance(
          targetHex,
          placementDecorationId,
          targetPoint.x,
          targetPoint.y,
        ),
      );
    }
  }

  function continueScatterStroke(
    pointer: { x: number; y: number },
    decorationId: string,
    radius: number,
    density: number,
    paintSet: OverlayPaintSet | null,
    brushEntry: DecorationBrushEntry | null,
    fallbackDecorationId: string | null,
  ) {
    const stroke = decorationPaintStrokeRef.current;
    if (!stroke) {
      return;
    }

    const previousPoint = stroke.lastScatterPoint;
    if (!previousPoint) {
      stroke.lastScatterPoint = pointer;
      scatterDecorationsAtPoint(
        pointer,
        decorationId,
        radius,
        density,
        true,
        paintSet,
        brushEntry,
        fallbackDecorationId,
      );
      return;
    }

    const spacing = getDecorationScatterSpacing(radius, density);
    const distance = getDistanceBetweenPoints(previousPoint, pointer);
    if (distance < spacing) {
      return;
    }

    const dx = pointer.x - previousPoint.x;
    const dy = pointer.y - previousPoint.y;
    let traveled = spacing;
    let lastPoint = previousPoint;

    while (traveled <= distance) {
      const ratio = traveled / distance;
      lastPoint = {
        x: previousPoint.x + dx * ratio,
        y: previousPoint.y + dy * ratio,
      };
      scatterDecorationsAtPoint(
        lastPoint,
        decorationId,
        radius,
        density,
        false,
        paintSet,
        brushEntry,
        fallbackDecorationId,
      );
      traveled += spacing;
    }

    stroke.lastScatterPoint = lastPoint;
  }

  function finishDecorationPaintStroke() {
    const stroke = decorationPaintStrokeRef.current;
    decorationPaintStrokeRef.current = null;
    if (!stroke || stroke.placements.length === 0) {
      return;
    }
    const historyEntry = createDecorationPlacementBatchHistoryEntry(stroke.placements);
    latestRef.current.pushHistory(historyEntry);
    getBroadcastMapChange()(historyEntry.after);
  }

  function snapshotDecorationRemovalForHex(
    targetHex: Hex,
    stroke: { before: MapChangeSnapshot; after: MapChangeSnapshot },
  ) {
    const decorations = getDecorationsForHex(targetHex);
    if (decorations.length === 0) {
      return;
    }

    stroke.before.decorations ??= {};
    stroke.after.decorations ??= {};

    for (const decoration of decorations) {
      if (!(decoration.id in stroke.before.decorations)) {
        stroke.before.decorations[decoration.id] = { ...decoration };
      }
      stroke.after.decorations[decoration.id] = null;
    }
  }

  function paintHexAt(hex: Hex, tileSetId: string, selectedEntry: OrderedBrushEntry | null = null) {
    const group = getTileSetById(tileSetId);
    if (!group) return;

    const key = hexKey(hex);
    const ms = latestRef.current.mapState;
    if (!ms.hexes[key]) return;

    const stroke = strokeRef.current;
    if (!stroke || stroke.paintedKeys.has(key)) return;

    stroke.before.hexes ??= {};
    stroke.after.hexes ??= {};
    stroke.before.hexes[key] = { ...ms.hexes[key] };
    stroke.paintedKeys.add(key);

    const existing = ms.hexes[key];
    const paintSelection = getPaintSelection(group, selectedEntry);
    if (!paintSelection) return;
    const newHex: HexData = {
      q: hex.q,
      r: hex.r,
      tileSetId: paintSelection.tileSetId,
      tileId: paintSelection.tileId,
      ...(existing?.roadEdges ? { roadEdges: existing.roadEdges } : {}),
    };
    stroke.after.hexes[key] = newHex;
    snapshotDecorationRemovalForHex(hex, stroke);

    latestRef.current.applyMapChanges({
      hexes: { [key]: newHex },
      decorations: stroke.after.decorations,
    });
  }

  function finishStroke() {
    const stroke = strokeRef.current;
    strokeRef.current = null;
    const changedHexCount = stroke?.before.hexes ? Object.keys(stroke.before.hexes).length : 0;
    const changedDecorationCount = stroke?.before.decorations
      ? Object.keys(stroke.before.decorations).length
      : 0;
    if (stroke && (changedHexCount > 0 || changedDecorationCount > 0)) {
      latestRef.current.pushHistory({
        before: stroke.before,
        after: stroke.after,
      });
      getBroadcastMapChange()(stroke.after);
    }
  }

  // --- Road helpers ---

  function addRoadEdge(hexData: HexData, edge: HexEdge): HexData {
    const existing = hexData.roadEdges ?? [];
    if (existing.includes(edge)) return hexData;
    return { ...hexData, roadEdges: [...existing, edge] };
  }

  function removeAllRoadEdges(hexData: HexData): HexData {
    const nextHex = { ...hexData };
    delete nextHex.roadEdges;
    return nextHex;
  }

  function snapshotForRoad(key: string) {
    const rs = roadStrokeRef.current;
    if (!rs) return;
    rs.before.hexes ??= {};
    if (rs.before.hexes[key]) return; // already snapshotted
    const ms = latestRef.current.mapState;
    const hex = ms.hexes[key];
    if (hex) rs.before.hexes[key] = { ...hex };
  }

  function applyRoadHex(key: string, hexData: HexData) {
    const rs = roadStrokeRef.current;
    if (!rs) return;
    rs.after.hexes ??= {};
    rs.after.hexes[key] = hexData;
    rs.modifiedKeys.add(key);
    latestRef.current.paintHexes([hexData]);
  }

  function finishRoadStroke() {
    const rs = roadStrokeRef.current;
    roadStrokeRef.current = null;
    if (rs && rs.before.hexes && Object.keys(rs.before.hexes).length > 0) {
      latestRef.current.pushHistory({
        before: rs.before,
        after: rs.after,
      });
      getBroadcastMapChange()(rs.after);
    }
  }

  function eraseDecorationAtPointer(wx: number, wy: number) {
    const eraseStroke = decorationEraseStrokeRef.current;
    if (!eraseStroke) {
      return;
    }

    const radius = latestRef.current.decorationScatterRadius;
    const targets = getDecorationEraseTargets(
      Object.values(latestRef.current.mapState.decorations),
      { x: wx, y: wy },
      radius,
    ).filter((decoration) => !eraseStroke.erasedDecorationIds.has(decoration.id));
    if (targets.length === 0) {
      return;
    }

    eraseStroke.before.decorations ??= {};
    eraseStroke.after.decorations ??= {};
    const changes: Record<string, null> = {};

    for (const target of targets) {
      eraseStroke.erasedDecorationIds.add(target.id);
      eraseStroke.before.decorations[target.id] = { ...target };
      eraseStroke.after.decorations[target.id] = null;
      changes[target.id] = null;
    }

    latestRef.current.applyMapChanges({
      decorations: changes,
    });
  }

  function finishDecorationEraseStroke() {
    const eraseStroke = decorationEraseStrokeRef.current;
    decorationEraseStrokeRef.current = null;
    if (eraseStroke && eraseStroke.before.decorations && Object.keys(eraseStroke.before.decorations).length > 0) {
      latestRef.current.pushHistory({
        before: eraseStroke.before,
        after: eraseStroke.after,
      });
      getBroadcastMapChange()(eraseStroke.after);
    }
  }

  // --- Edge scroll paint callback ---

  function handleEdgeScrollPaint() {
    const es = edgeScrollRef.current;
    if (!es) return;
    const mouse = es.lastMouseRef.current;
    if (mouse && strokeRef.current) {
      const { wx, wy } = screenToWorld(
        canvasRef.current!,
        cameraRef.current,
        mouse.clientX,
        mouse.clientY,
      );
      const hex = pixelToHex(wx, wy);
      const drag = dragRef.current;
      const { selectedTileSetId: brush, selectedBrushEntry } = latestRef.current;
      if (drag) {
        const paintTileSetId = getPaintTileSetId(drag.button, brush);
        if (paintTileSetId) {
          paintHexAt(hex, paintTileSetId, paintTileSetId === brush ? selectedBrushEntry : null);
        }
      }
    }
  }

  // --- Mouse handlers ---

  function handleMouseDown(e: React.MouseEvent) {
    e.preventDefault();

    if (spaceHeldRef.current) return;

    const {
      selectedTileSetId: brush,
      selectedBrushEntry,
      selectedDecorationId,
      selectedDecorationPaintSetId,
      selectedDecorationBrushEntry,
      decorationMode,
      decorationScatterRadius,
      decorationScatterDensity,
      activeTool,
    } = latestRef.current;

    const activeDecorationPaintSet = selectedDecorationPaintSetId
      ? getDecorationPaintSetById(selectedDecorationPaintSetId) ?? null
      : null;
    const decorationSelectionActive = Boolean(
      selectedDecorationPaintSetId || selectedDecorationId,
    );

    if (decorationSelectionActive) {
      const { wx, wy } = screenToWorld(
        canvasRef.current!,
        cameraRef.current,
        e.clientX,
        e.clientY,
      );
      const hex = pixelToHex(wx, wy);
      const key = hexKey(hex);
      if (!latestRef.current.mapState.hexes[key]) {
        return;
      }

      if (e.button === 0 && (decorationMode === "single" || decorationMode === "brush")) {
        decorationPaintStrokeRef.current = {
          placements: [],
          placementKeys: new Set(),
          lastScatterPoint: { x: wx, y: wy },
        };
        const placementDecorationId = resolveActiveDecorationId(
          activeDecorationPaintSet,
          selectedDecorationBrushEntry,
          selectedDecorationId,
        );
        if (!placementDecorationId) {
          return;
        }
        if (decorationMode === "single") {
          placeSingleDecorationAtPoint({ x: wx, y: wy }, placementDecorationId);
        } else {
          scatterDecorationsAtPoint(
            { x: wx, y: wy },
            placementDecorationId,
            decorationScatterRadius,
            decorationScatterDensity,
            true,
            activeDecorationPaintSet,
            selectedDecorationBrushEntry,
            selectedDecorationId,
          );
        }
        return;
      }

      if (e.button === 0 && decorationMode === "erase") {
        decorationEraseStrokeRef.current = {
          before: {},
          after: {},
          erasedDecorationIds: new Set(),
        };
        eraseDecorationAtPointer(wx, wy);
        return;
      }
    }

    // Eyedropper tool: pick tile and switch to brush mode
    if (e.button === 0 && activeTool === "eyedropper") {
      const { wx, wy } = screenToWorld(
        canvasRef.current!,
        cameraRef.current,
        e.clientX,
        e.clientY,
      );
      const hex = pixelToHex(wx, wy);
      const key = hexKey(hex);
      const hexData = latestRef.current.mapState.hexes[key];
      if (hexData?.tileSetId) {
        latestRef.current.selectBrush(hexData.tileSetId, hexData.tileId);
      }
      return;
    }

    // Road tool
    if (activeTool === "road") {
      const { wx, wy } = screenToWorld(
        canvasRef.current!,
        cameraRef.current,
        e.clientX,
        e.clientY,
      );
      const hex = pixelToHex(wx, wy);
      const key = hexKey(hex);
      const ms = latestRef.current.mapState;
      if (!ms.hexes[key]) return;

      if (e.button === 0) {
        roadStrokeRef.current = {
          before: {},
          after: {},
          lastHex: hex,
          modifiedKeys: new Set(),
        };
      } else if (e.button === 2) {
        const hexData = ms.hexes[key];
        if (!hexData?.roadEdges || hexData.roadEdges.length === 0) return;

        roadStrokeRef.current = {
          before: {},
          after: {},
          lastHex: null,
          modifiedKeys: new Set(),
        };

        snapshotForRoad(key);
        applyRoadHex(key, removeAllRoadEdges(hexData));

        const neighbors = hexNeighbors(hex);
        for (const neighbor of neighbors) {
          const nKey = hexKey(neighbor);
          const nHex = ms.hexes[nKey];
          if (!nHex?.roadEdges) continue;
          const edge = getEdgeBetween(hex, neighbor);
          if (!edge) continue;
          const oppositeEdge = OPPOSITE_EDGE[edge];
          if (nHex.roadEdges.includes(oppositeEdge)) {
            snapshotForRoad(nKey);
            const updated = {
              ...nHex,
              roadEdges: nHex.roadEdges.filter((e) => e !== oppositeEdge),
            };
            if (updated.roadEdges.length === 0) {
              applyRoadHex(nKey, removeAllRoadEdges(updated));
            } else {
              applyRoadHex(nKey, updated);
            }
          }
        }

        finishRoadStroke();
      }
      return;
    }

    if (activeTool !== "tile-brush") return;

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      camX: cameraRef.current.x,
      camY: cameraRef.current.y,
      moved: 0,
      button: e.button,
    };

    const paintTileSetId = getPaintTileSetId(e.button, brush);
    if (paintTileSetId) {
      strokeRef.current = {
        before: {},
        after: {},
        paintedKeys: new Set(),
      };
      const { wx, wy } = screenToWorld(
        canvasRef.current!,
        cameraRef.current,
        e.clientX,
        e.clientY,
      );
      const hex = pixelToHex(wx, wy);
      paintHexAt(
        hex,
        paintTileSetId,
        paintTileSetId === brush ? selectedBrushEntry : null,
      );
    }
  }

  function handleMouseMove(e: React.MouseEvent) {
    const es = edgeScrollRef.current;
    const prevMouse = es?.lastMouseRef.current ?? null;
    if (es) {
      es.lastMouseRef.current = { clientX: e.clientX, clientY: e.clientY };
    }

    const { wx, wy } = screenToWorld(
      canvasRef.current!,
      cameraRef.current,
      e.clientX,
      e.clientY,
    );
    pointerWorldRef.current = {
      x: wx,
      y: wy,
      inside: true,
    };
    const previewHex = pixelToHex(wx, wy);
    const previewKey = hexKey(previewHex);
    const previewHexData = latestRef.current.mapState.hexes[previewKey];

    if (latestRef.current.activeTool === "eyedropper") {
      setCursorPreview({
        clientX: e.clientX,
        clientY: e.clientY,
        visible: true,
        hoveredTileSetId: previewHexData?.tileSetId ?? null,
      });
    } else {
      setCursorPreview((prev) =>
        prev.visible
          ? {
              ...prev,
              visible: false,
              hoveredTileSetId: null,
            }
          : prev,
      );
    }

    // Space-held panning
    if (spaceHeldRef.current) {
      if (prevMouse) {
        cameraRef.current.x += e.clientX - prevMouse.clientX;
        cameraRef.current.y += e.clientY - prevMouse.clientY;
        canvasRef.current!.style.cursor = "grabbing";
        draw();
      }
      return;
    }

    // Road tool drag
    const rs = roadStrokeRef.current;
    if (rs && rs.lastHex) {
      const hex = pixelToHex(wx, wy);
      const key = hexKey(hex);
      const ms = latestRef.current.mapState;

      if (
        ms.hexes[key] &&
        (hex.q !== rs.lastHex.q || hex.r !== rs.lastHex.r)
      ) {
        const edge = getEdgeBetween(rs.lastHex, hex);
        if (edge) {
          const lastKey = hexKey(rs.lastHex);
          const oppositeEdge = OPPOSITE_EDGE[edge];

          const lastHexData = rs.after.hexes?.[lastKey] ?? ms.hexes[lastKey];
          const curHexData = rs.after.hexes?.[key] ?? ms.hexes[key];

          snapshotForRoad(lastKey);
          snapshotForRoad(key);

          const updatedLast = addRoadEdge(lastHexData, edge);
          const updatedCur = addRoadEdge(curHexData, oppositeEdge);

          applyRoadHex(lastKey, updatedLast);
          applyRoadHex(key, updatedCur);

          rs.lastHex = hex;
        }
      }
    }

    const drag = dragRef.current;
    if (drag) {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      drag.moved = Math.max(drag.moved, Math.abs(dx) + Math.abs(dy));

      if (strokeRef.current) {
        const hex = pixelToHex(wx, wy);
        const { selectedTileSetId: brush, selectedBrushEntry } = latestRef.current;
        const paintTileSetId = getPaintTileSetId(drag.button, brush);
        if (paintTileSetId) {
          paintHexAt(
            hex,
            paintTileSetId,
            paintTileSetId === brush ? selectedBrushEntry : null,
          );
        }
      }
    }

    if (
      decorationPaintStrokeRef.current &&
      latestRef.current.decorationMode === "brush"
    ) {
      const ongoingPaintSet = latestRef.current.selectedDecorationPaintSetId
        ? getDecorationPaintSetById(latestRef.current.selectedDecorationPaintSetId) ?? null
        : null;
      const ongoingDecorationId = resolveActiveDecorationId(
        ongoingPaintSet,
        latestRef.current.selectedDecorationBrushEntry,
        latestRef.current.selectedDecorationId,
      );
      if (ongoingDecorationId) {
        continueScatterStroke(
          { x: wx, y: wy },
          ongoingDecorationId,
          latestRef.current.decorationScatterRadius,
          latestRef.current.decorationScatterDensity,
          ongoingPaintSet,
          latestRef.current.selectedDecorationBrushEntry,
          latestRef.current.selectedDecorationId,
        );
      }
    }

    if (decorationEraseStrokeRef.current) {
      eraseDecorationAtPointer(wx, wy);
    }

    es?.updateEdgeScroll(e.clientX, e.clientY);

    // Hover detection
    const prev = hoveredHexRef.current;
    hoveredHexRef.current =
      previewHexData && shouldHighlightHoveredHex(latestRef.current.activeTool)
        ? previewKey
        : null;
    const hasPlacementPreview =
      Boolean(latestRef.current.selectedDecorationId) ||
      latestRef.current.activeTool === "map-note";
    if (hoveredHexRef.current !== prev || hasPlacementPreview) {
      draw();
    }
  }

  function handleMouseUp() {
    const drag = dragRef.current;
    dragRef.current = null;

    if (drag && (drag.button === 0 || drag.button === 2)) {
      finishStroke();
    }

    if (roadStrokeRef.current) {
      finishRoadStroke();
    }

    if (decorationPaintStrokeRef.current) {
      finishDecorationPaintStroke();
    }

    if (decorationEraseStrokeRef.current) {
      finishDecorationEraseStroke();
    }
  }

  function handleMouseLeave() {
    const es = edgeScrollRef.current;
    finishStroke();
    finishRoadStroke();
    finishDecorationPaintStroke();
    finishDecorationEraseStroke();
    dragRef.current = null;
    hoveredHexRef.current = null;
    pointerWorldRef.current.inside = false;
    if (es) es.lastMouseRef.current = null;
    setCursorPreview((prev) => ({
      ...prev,
      visible: false,
      hoveredTileSetId: null,
    }));
    es?.stopEdgeScroll();
    updateCursor();
    draw();
  }

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleEdgeScrollPaint,
  };
}
